/* Graph-backed providers — every area, no mock fallback.
 *
 * A provider set that was half real and half fixture was worse than either: an
 * empty register sitting beside convincing fake data reads as a broken app, or
 * gets believed. So in 'sharepoint' data mode nothing here falls back — an area
 * with no rows shows as empty, which is the truth.
 *
 * All access is through Graph's SharePoint endpoints rather than the SharePoint
 * REST API: one resource, one token, one client (§5). Field mapping lives in
 * mappers.ts; the columns it names are created by
 * provisioning/Provision-LedgerSite.ps1 and kept in step by
 * `npm run check:schema`.
 */

import {
  allRoleGroups,
  grantsFor,
  levelFromGroups,
  planAppOverride,
  planLevelChange,
  planRevokeAll,
  type AppRole,
  type HubLevel,
} from '@hird/access';
import { DirectoryClient } from '../graph/DirectoryClient';
import { JoinerClient, type IJoinerSite } from '../graph/JoinerClient';
import { TrainingScopeClient, type ITrainingSite } from '../graph/TrainingScopeClient';
import { LeaveSyncClient } from '../graph/LeaveSyncClient';
import { OffboardClient } from '../graph/OffboardClient';
import { OnboardingClient } from '../onboarding/OnboardingClient';
import type {
  AssignmentReason,
  DepotId,
  IApplication,
  IArticle,
  IAssignment,
  IAttachment,
  IAuditEntry,
  IBackup,
  IChecklistRun,
  IChecklistTemplate,
  ICredential,
  ICurrentUser,
  ICustomRecord,
  IDepot,
  IDevice,
  IDomain,
  IGroup,
  ILicenceSku,
  ILocation,
  ILocations,
  IModule,
  INetwork,
  INetworks,
  IPerson,
  IRecordType,
  IRecoveryPlan,
  IRestoreTest,
  IRoleDefinition,
  IStatusTile,
  ISupplier,
} from '../../models';
import type {
  IAccessService,
  IApplicationService,
  IArticleService,
  IAssignResult,
  IAttachmentService,
  IAuditService,
  IBackupService,
  ICredentialService,
  IDepotService,
  IDeviceService,
  IDirectoryAccess,
  IDirectoryService,
  ITrainingScopeService,
  ITrainingScope,
  TrainingScopeRefusal,
  IDomainService,
  IGroupService,
  ILedgerServices,
  ILicenceService,
  ILocationService,
  INetworkService,
  IPeopleService,
  IProcedureService,
  IRecordTypeService,
  ISettingsService,
  IStatusService,
  ISupplierService,
  ISyncService,
} from '../IServices';
import { DEPOTS, depotName } from '../depots';
import { GraphClient, type TokenProvider } from './GraphClient';
import { withPromotions } from '../../lib/promote';
import { LISTS } from './lists';
import * as map from './mappers';

/* ─────────────────────────── Register ─────────────────────────── */

/* Purpose is the only writable column, so there is no create and no remove. A
   group typed in by hand would be one Entra does not have, and deleting the row
   would not delete the group — it would only hide the access it grants. */
class GroupRepository implements IGroupService {
  constructor(private readonly g: GraphClient) {}

  async all(): Promise<IGroup[]> {
    return (await this.g.listItems(LISTS.groups)).map((i) => map.toGroup(i.id, i.fields));
  }
  async update(id: string, patch: Partial<IGroup>): Promise<IGroup> {
    await this.g.updateItem(LISTS.groups, id, map.groupFields(patch));
    const item = await this.g.getItem(LISTS.groups, id);
    return map.toGroup(item.id, item.fields);
  }
}

class PeopleRepository implements IPeopleService {
  constructor(private readonly g: GraphClient) {}

  async list(): Promise<IPerson[]> {
    return (await this.g.listItems(LISTS.people)).map((i) => map.toPerson(i.id, i.fields));
  }
  async get(id: string): Promise<IPerson | null> {
    const item = await this.g.getItem(LISTS.people, id).catch(() => null);
    return item ? map.toPerson(item.id, item.fields) : null;
  }
  /* Notes only. Every other column is sync-owned, and writing one from the
     browser means the 02:00 Function silently reverts it (§10 rule 1). */
  async saveNotes(id: string, notes: string): Promise<void> {
    await this.g.updateItem(LISTS.people, id, { Notes: notes });
  }
  async create(person: Omit<IPerson, 'id'>): Promise<IPerson> {
    const item = await this.g.createItem(LISTS.people, map.personFields(person));
    return map.toPerson(item.id, item.fields);
  }
  /* A full update, unlike saveNotes. Everything except Notes is sync-owned, so
     what this writes is a stopgap until the 02:00 Function sees the account —
     the screen says so rather than the repository refusing it. */
  async update(id: string, patch: Partial<IPerson>): Promise<IPerson> {
    await this.g.updateItem(LISTS.people, id, map.personFields(patch));
    const item = await this.g.getItem(LISTS.people, id);
    return map.toPerson(item.id, item.fields);
  }
  async remove(id: string): Promise<void> {
    await this.g.deleteItem(LISTS.people, id);
  }
}

class DeviceRepository implements IDeviceService {
  constructor(private readonly g: GraphClient) {}

  async list(): Promise<IDevice[]> {
    return (await this.g.listItems(LISTS.devices)).map((i) => map.toDevice(i.id, i.fields));
  }
  async get(id: string): Promise<IDevice | null> {
    const item = await this.g.getItem(LISTS.devices, id).catch(() => null);
    return item ? map.toDevice(item.id, item.fields) : null;
  }
  /** Title is indexed, so this filter is safe. The QR label route depends on it. */
  async getByTag(tag: string): Promise<IDevice | null> {
    const items = await this.g.filterItems(
      LISTS.devices,
      `fields/Title eq '${tag.replace(/'/g, "''")}'`,
    );
    const first = items[0];
    return first ? map.toDevice(first.id, first.fields) : null;
  }
  async create(device: Omit<IDevice, 'id'>): Promise<IDevice> {
    const item = await this.g.createItem(LISTS.devices, map.deviceFields(device));
    return map.toDevice(item.id, item.fields);
  }
  async update(id: string, patch: Partial<IDevice>): Promise<IDevice> {
    await this.g.updateItem(LISTS.devices, id, map.deviceFields(patch));
    const updated = await this.get(id);
    if (!updated) throw new Error(`Device ${id} vanished mid-update`);
    return updated;
  }
  async remove(id: string): Promise<void> {
    await this.g.deleteItem(LISTS.devices, id);
  }

  async assignments(): Promise<IAssignment[]> {
    const rows = (await this.g.listItems(LISTS.assignments)).map((i) =>
      map.toAssignment(i.id, i.fields),
    );
    // Newest first — "who has it now" and "who had it last" are the top two.
    return rows.sort((a, b) => (b.from || '').localeCompare(a.from || ''));
  }

  /** Close the open spell, move the pointer, open the new one. In that order.
   *
   *  The order is the whole design. AssignedTo is what PeopleScreen, the
   *  Devices unassigned lens and offboardingGaps all read, so it goes in the
   *  middle: if the run dies before it, nothing has changed and the register is
   *  consistent; if it dies after it, the pointer is right and one history row
   *  is missing. A missing history row loses a fact. A stale OPEN row would
   *  invent a current holder who is not one, and that is the version somebody
   *  acts on.
   *
   *  Nothing throws for a partial failure — the result says which half ran, and
   *  historyDisagrees() surfaces the mismatch on the device itself rather than
   *  leaving it to be discovered. */
  async assign(
    deviceId: string,
    personId: string | null,
    reason: AssignmentReason,
    when: string = new Date().toISOString().slice(0, 10),
  ): Promise<IAssignResult> {
    const problems: string[] = [];
    let closed: IAssignment | undefined;
    let opened: IAssignment | undefined;

    const open = (await this.assignments()).filter((a) => a.device === deviceId && !a.to);
    for (const spell of open) {
      try {
        await this.g.updateItem(LISTS.assignments, spell.id, map.assignmentFields({ to: when }));
        closed = { ...spell, to: when };
      } catch (err) {
        problems.push(
          `Could not close the open record of ${spell.person} holding this: ${msg(err)}`,
        );
      }
    }

    let device: IDevice;
    try {
      await this.g.updateItem(LISTS.devices, deviceId, map.deviceFields({ user: personId }));
    } catch (err) {
      problems.push(`Could not change who the device is assigned to: ${msg(err)}`);
    }
    const after = await this.g.getItem(LISTS.devices, deviceId);
    device = map.toDevice(after.id, after.fields);

    if (personId) {
      try {
        const item = await this.g.createItem(
          LISTS.assignments,
          map.assignmentFields({ device: deviceId, person: personId, from: when, reason, notes: '' }),
        );
        opened = map.toAssignment(item.id, item.fields);
      } catch (err) {
        problems.push(`Assigned, but the history record was not written: ${msg(err)}`);
      }
    }

    return { device, closed, opened, problems };
  }
}

function msg(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/* Hub levels and per-app access, written into Entra.
 *
 * Every decision here comes from @hird/access, which the hub calls too — so the
 * two apps cannot drift into granting different things from the same level. The
 * only thing this class decides is the ORDER, and it does not decide that
 * either: the planner returns `leave` and `join` separately precisely so a
 * caller cannot get it the wrong way round.
 *
 * Leave before join, always. Levels are exclusive and a leftover membership
 * silently out-ranks the new one, so a failure part-way through must leave
 * somebody with LESS access than intended, never more.
 */
class TrainingScopeRepository implements ITrainingScopeService {
  constructor(private readonly c: TrainingScopeClient) {}

  departments(): Promise<string[]> {
    return this.c.departments();
  }

  get(email: string, name?: string): Promise<ITrainingScope | TrainingScopeRefusal> {
    return this.c.get(email, name);
  }

  async set(itemId: string, departments: string[]): Promise<void> {
    /* The refusal lives here, not only in the dialog. A scope of nothing is
       an Admin who can see nothing, which should be done by changing their
       tier rather than by leaving the boxes unticked. */
    if (!departments.length) {
      throw new Error('Pick at least one department, or change their access level instead.');
    }
    await this.c.set(itemId, departments);
  }
}

class DirectoryRepository implements IDirectoryService {
  constructor(private readonly d: DirectoryClient) {}

  async get(graphId: string): Promise<IDirectoryAccess> {
    const memberOf = await this.held(graphId);
    const level = levelFromGroups(memberOf);
    return { level, grants: grantsFor(level, memberOf), memberOf };
  }

  async setLevel(graphId: string, level: HubLevel | null): Promise<IDirectoryAccess> {
    /* Read live, never from the nightly sync's copy. The rule that an explicit
       per-app choice is left alone is decided from this, so a stale answer
       would quietly undo an override somebody made this morning. */
    const memberOf = level ? await this.held(graphId) : [];
    await this.apply(graphId, planLevelChange(level, memberOf));
    return this.get(graphId);
  }

  async setAppRole(graphId: string, appId: string, role: AppRole | null): Promise<IDirectoryAccess> {
    const memberOf = role === null ? await this.held(graphId) : [];
    await this.apply(graphId, planAppOverride(appId, role, memberOf));
    return this.get(graphId);
  }

  async revokeAll(graphId: string): Promise<IDirectoryAccess> {
    await this.apply(graphId, planRevokeAll(await this.held(graphId)));
    return this.get(graphId);
  }

  /** Which of the model's groups this person is actually in.
   *
   *  Asked as "who is in these groups" rather than "what groups is this person
   *  in", because Blueprint already knows their graphId and the second question
   *  needs two more directory permissions to ask. */
  private async held(graphId: string): Promise<string[]> {
    const members = await this.d.membersOf(allRoleGroups());
    return [...members.entries()]
      .filter(([, ids]) => ids.has(graphId))
      .map(([groupId]) => groupId);
  }

  private async apply(graphId: string, plan: { leave: string[]; join: string[] }): Promise<void> {
    for (const id of plan.leave) await this.d.removeMember(id, graphId);
    for (const id of plan.join) await this.d.addMember(id, graphId);
  }
}

class LicenceRepository implements ILicenceService {
  constructor(private readonly g: GraphClient) {}
  async skus(): Promise<ILicenceSku[]> {
    return (await this.g.listItems(LISTS.licences)).map((i) => map.toLicence(i.id, i.fields));
  }
  /* The item id, not the SkuId. toLicence maps ILicenceSku.id from SkuId
     because that is what People.Licences joins on, so the list item has to be
     found by it rather than addressed directly. */
  async update(id: string, patch: Partial<ILicenceSku>): Promise<ILicenceSku> {
    const items = await this.g.listItems(LISTS.licences);
    const item = items.find((i) => String(i.fields.SkuId ?? i.id) === id || i.id === id);
    if (!item) throw new Error(`No licence ${id}`);
    await this.g.updateItem(LISTS.licences, item.id, map.licenceFields(patch));
    const fresh = await this.g.getItem(LISTS.licences, item.id);
    return map.toLicence(fresh.id, fresh.fields);
  }
}

/* ─────────────────────────── Infrastructure ─────────────────────────── */

const EMPTY_NETWORK: INetwork = {
  provider: '',
    wan: '',
    wanType: '',
    speed: '', wanAcct: '', wanSupport: '', wanEnds: '', failover: '', block: '', dns: '',
  subnets: [], ssids: [], vpn: [],
};

const EMPTY_LOCATION: ILocation = {
  short: '',
  addr: '',
  postcode: '',
  phone: '',
  role: '',
  contact: '',
  comms: '',
  access: '',
  hours: '',
  notes: '',
};

/* Keyed on the depot name in Title, exactly as the network is — one row per
   site, nothing to create or delete, and a site with no row yet reads as empty
   rather than undefined so the screen offers a form instead of throwing. */
class LocationRepository implements ILocationService {
  constructor(private readonly g: GraphClient) {}

  async all(): Promise<ILocations> {
    const items = await this.g.listItems(LISTS.locations);
    /* Whatever rows exist, keyed by the site name. It used to seed the three
       known depots and ignore anything else, which meant a fourth site could
       be added to the list and the app would not see it. */
    const out: ILocations = {};
    for (const item of items) {
      const name = String(item.fields.Title ?? '').trim();
      if (name) out[name] = map.toLocation(item.fields);
    }
    /* The seed sites always appear, so a tenant whose list has not been filled
       in yet still has somewhere to put people. */
    for (const depot of DEPOTS) if (!out[depot.id]) out[depot.id] = EMPTY_LOCATION;
    return out;
  }

  async create(name: string): Promise<ILocation> {
    const created = await this.g.createItem(LISTS.locations, { Title: name.trim() });
    return map.toLocation(created.fields);
  }

  async save(depot: DepotId, patch: Partial<ILocation>): Promise<ILocation> {
    const name = depot;
    const items = await this.g.listItems(LISTS.locations);
    const existing = items.find(
      (i) => String(i.fields.Title ?? '').toLowerCase() === name.toLowerCase(),
    );

    if (existing) {
      await this.g.updateItem(LISTS.locations, existing.id, map.locationFields(patch));
      const item = await this.g.getItem(LISTS.locations, existing.id);
      return map.toLocation(item.fields);
    }

    const created = await this.g.createItem(LISTS.locations, {
      Title: name,
      ...map.locationFields(patch),
    });
    return map.toLocation(created.fields);
  }
}

class NetworkRepository implements INetworkService {
  constructor(private readonly g: GraphClient) {}

  /* One item per depot, keyed by the Title column holding the depot name. A
     depot with no row yields an empty network rather than undefined, so the
     screen renders "nothing documented" instead of throwing. */
  async all(): Promise<INetworks> {
    const items = await this.g.listItems(LISTS.networks);
    const out = { hull: EMPTY_NETWORK, don: EMPTY_NETWORK, lon: EMPTY_NETWORK } as INetworks;
    for (const item of items) {
      const depot = DEPOTS.find(
        (d) => d.name.toLowerCase() === String(item.fields.Title ?? '').toLowerCase(),
      );
      if (depot) out[depot.id] = map.toNetwork(item.fields);
    }
    return out;
  }

  /* Keyed on the depot name in Title, because there is no other stable handle —
     the list holds one item per depot and nothing carries an id the app knows.
     A depot with no row yet gets one created rather than failing to save. */
  async save(depot: DepotId, patch: Partial<INetwork>): Promise<INetwork> {
    const name = DEPOTS.find((d) => d.id === depot)?.name ?? depot;
    const items = await this.g.listItems(LISTS.networks);
    const existing = items.find(
      (i) => String(i.fields.Title ?? '').toLowerCase() === name.toLowerCase(),
    );

    if (existing) {
      await this.g.updateItem(LISTS.networks, existing.id, map.networkFields(patch));
      const item = await this.g.getItem(LISTS.networks, existing.id);
      return map.toNetwork(item.fields);
    }

    const item = await this.g.createItem(LISTS.networks, {
      Title: name,
      ...map.networkFields(patch),
    });
    return map.toNetwork(item.fields);
  }
}

class ApplicationRepository implements IApplicationService {
  constructor(private readonly g: GraphClient) {}
  async list(): Promise<IApplication[]> {
    return (await this.g.listItems(LISTS.applications)).map((i) =>
      map.toApplication(i.id, i.fields),
    );
  }
  async create(application: Omit<IApplication, 'id'>): Promise<IApplication> {
    const item = await this.g.createItem(LISTS.applications, map.applicationFields(application));
    return map.toApplication(item.id, item.fields);
  }
  async update(id: string, patch: Partial<IApplication>): Promise<IApplication> {
    await this.g.updateItem(LISTS.applications, id, map.applicationFields(patch));
    const item = await this.g.getItem(LISTS.applications, id);
    return map.toApplication(item.id, item.fields);
  }
  async remove(id: string): Promise<void> {
    await this.g.deleteItem(LISTS.applications, id);
  }
}

class SupplierRepository implements ISupplierService {
  constructor(private readonly g: GraphClient) {}
  async list(): Promise<ISupplier[]> {
    return (await this.g.listItems(LISTS.suppliers)).map((i) => map.toSupplier(i.id, i.fields));
  }
  async create(supplier: Omit<ISupplier, 'id'>): Promise<ISupplier> {
    const item = await this.g.createItem(LISTS.suppliers, map.supplierFields(supplier));
    return map.toSupplier(item.id, item.fields);
  }
  async update(id: string, patch: Partial<ISupplier>): Promise<ISupplier> {
    await this.g.updateItem(LISTS.suppliers, id, map.supplierFields(patch));
    const item = await this.g.getItem(LISTS.suppliers, id);
    return map.toSupplier(item.id, item.fields);
  }
  async remove(id: string): Promise<void> {
    await this.g.deleteItem(LISTS.suppliers, id);
  }
}

class DomainRepository implements IDomainService {
  constructor(private readonly g: GraphClient) {}
  async list(): Promise<IDomain[]> {
    return (await this.g.listItems(LISTS.domains)).map((i) => map.toDomain(i.id, i.fields));
  }
  async create(domain: Omit<IDomain, 'id'>): Promise<IDomain> {
    const item = await this.g.createItem(LISTS.domains, map.domainFields(domain));
    return map.toDomain(item.id, item.fields);
  }
  async update(id: string, patch: Partial<IDomain>): Promise<IDomain> {
    await this.g.updateItem(LISTS.domains, id, map.domainFields(patch));
    const item = await this.g.getItem(LISTS.domains, id);
    return map.toDomain(item.id, item.fields);
  }
  async remove(id: string): Promise<void> {
    await this.g.deleteItem(LISTS.domains, id);
  }
}

class BackupRepository implements IBackupService {
  constructor(private readonly g: GraphClient) {}
  async jobs(): Promise<IBackup[]> {
    return (await this.g.listItems(LISTS.backups)).map((i) => map.toBackup(i.id, i.fields));
  }
  async saveJob(job: IBackup): Promise<IBackup> {
    const fields = map.backupFields(job);
    if (job.id) {
      await this.g.updateItem(LISTS.backups, job.id, fields);
      const item = await this.g.getItem(LISTS.backups, job.id);
      return map.toBackup(item.id, item.fields);
    }
    const item = await this.g.createItem(LISTS.backups, fields);
    return map.toBackup(item.id, item.fields);
  }
  async removeJob(id: string): Promise<void> {
    await this.g.deleteItem(LISTS.backups, id);
  }
  async savePlan(plan: IRecoveryPlan): Promise<IRecoveryPlan> {
    const fields = map.recoveryPlanFields(plan);
    if (plan.id) {
      await this.g.updateItem(LISTS.recoveryPlans, plan.id, fields);
      const item = await this.g.getItem(LISTS.recoveryPlans, plan.id);
      return map.toRecoveryPlan(item.id, item.fields);
    }
    const item = await this.g.createItem(LISTS.recoveryPlans, fields);
    return map.toRecoveryPlan(item.id, item.fields);
  }
  async removePlan(id: string): Promise<void> {
    await this.g.deleteItem(LISTS.recoveryPlans, id);
  }
  async plans(): Promise<IRecoveryPlan[]> {
    return (await this.g.listItems(LISTS.recoveryPlans)).map((i) =>
      map.toRecoveryPlan(i.id, i.fields),
    );
  }
  async tests(): Promise<IRestoreTest[]> {
    const rows = (await this.g.listItems(LISTS.restoreTests)).map((i) =>
      map.toRestoreTest(i.fields),
    );
    return rows.sort((a, b) => (a.when < b.when ? 1 : -1));
  }
  async logTest(test: IRestoreTest): Promise<void> {
    await this.g.createItem(LISTS.restoreTests, map.restoreTestFields(test));
  }
}

/* ─────────────────────────── Documentation ─────────────────────────── */

class ArticleRepository implements IArticleService {
  constructor(private readonly g: GraphClient) {}
  async list(): Promise<IArticle[]> {
    return (await this.g.listItems(LISTS.articles)).map((i) => map.toArticle(i.id, i.fields));
  }
  async get(id: string): Promise<IArticle | null> {
    const item = await this.g.getItem(LISTS.articles, id).catch(() => null);
    return item ? map.toArticle(item.id, item.fields) : null;
  }
  async save(article: IArticle): Promise<IArticle> {
    const fields = map.articleFields({
      ...article,
      upd: new Date().toISOString().slice(0, 10),
    });
    if (article.id) {
      await this.g.updateItem(LISTS.articles, article.id, fields);
      const item = await this.g.getItem(LISTS.articles, article.id);
      return map.toArticle(item.id, item.fields);
    }
    const item = await this.g.createItem(LISTS.articles, fields);
    return map.toArticle(item.id, item.fields);
  }
  /** Resets the 120-day review clock without touching the body (§17). */
  async markReviewed(id: string): Promise<void> {
    await this.g.updateItem(LISTS.articles, id, {
      ReviewedOn: new Date().toISOString().slice(0, 10),
    });
  }
}

class ProcedureRepository implements IProcedureService {
  constructor(private readonly g: GraphClient) {}
  async templates(): Promise<IChecklistTemplate[]> {
    return (await this.g.listItems(LISTS.checklistTemplates)).map((i) =>
      map.toTemplate(i.id, i.fields),
    );
  }
  async runs(): Promise<IChecklistRun[]> {
    return (await this.g.listItems(LISTS.checklistRuns)).map((i) => map.toRun(i.id, i.fields));
  }
  async startRun(templateId: string, subject: string): Promise<IChecklistRun> {
    const item = await this.g.createItem(LISTS.checklistRuns, {
      Title: subject,
      TemplateRefLookupId: templateId,
      StartedOn: new Date().toISOString().slice(0, 10),
      CompletedJson: '[]',
    });
    return map.toRun(item.id, item.fields);
  }
  async toggleStep(runId: string, stepIndex: number, done: boolean): Promise<void> {
    const item = await this.g.getItem(LISTS.checklistRuns, runId);
    const run = map.toRun(item.id, item.fields);
    const next = done
      ? [...new Set([...run.done, stepIndex])].sort((a, b) => a - b)
      : run.done.filter((i) => i !== stepIndex);
    await this.g.updateItem(LISTS.checklistRuns, runId, { CompletedJson: JSON.stringify(next) });
  }
}

class CredentialRepository implements ICredentialService {
  constructor(private readonly g: GraphClient) {}

  async list(): Promise<ICredential[]> {
    return (await this.g.listItems(LISTS.credentials)).map((i) => map.toCredential(i.id, i.fields));
  }


  async create(credential: Omit<ICredential, 'id'>): Promise<ICredential> {
    const item = await this.g.createItem(LISTS.credentials, map.credentialFields(credential));
    return map.toCredential(item.id, item.fields);
  }
  async update(id: string, patch: Partial<ICredential>): Promise<ICredential> {
    await this.g.updateItem(LISTS.credentials, id, map.credentialFields(patch));
    const item = await this.g.getItem(LISTS.credentials, id);
    return map.toCredential(item.id, item.fields);
  }
  async remove(id: string): Promise<void> {
    await this.g.deleteItem(LISTS.credentials, id);
  }
  async markRotated(id: string): Promise<void> {
    await this.g.updateItem(LISTS.credentials, id, {
      RotatedOn: new Date().toISOString().slice(0, 10),
    });
  }
}

/* Append-only in practice: everyone has Read on this list and nobody has
   Contribute, so a browser write fails by design. Writes go through the sync
   Function's app identity (§6.3). The log() below therefore attempts the write
   and swallows a permission failure rather than breaking the action that
   triggered it — an audit entry that cannot be written must not stop a device
   from being saved. */
class AuditRepository implements IAuditService {
  constructor(private readonly g: GraphClient) {}

  async list(take = 100): Promise<IAuditEntry[]> {
    const items = await this.g.listItems(
      LISTS.auditLog,
      '$expand=fields&$top=200&$orderby=fields/OccurredAt desc',
    );
    return items.slice(0, take).map((i) => map.toAuditEntry(i.fields));
  }

  async log(entry: Omit<IAuditEntry, 'when'>): Promise<void> {
    try {
      await this.g.createItem(LISTS.auditLog, map.auditFields(entry));
    } catch {
      // Expected for non-admins. The Function owns this list.
    }
  }
}

/* ─────────────────────────── Custom records ─────────────────────────── */

class RecordTypeRepository implements IRecordTypeService {
  constructor(private readonly g: GraphClient) {}

  async types(): Promise<IRecordType[]> {
    const rows = (await this.g.listItems(LISTS.recordTypes)).map((i) =>
      map.toRecordType(i.id, i.fields),
    );
    return rows.sort((a, b) => a.sortOrder - b.sortOrder);
  }
  async saveType(type: IRecordType): Promise<IRecordType> {
    const fields = map.recordTypeFields(type);
    if (type.id) {
      await this.g.updateItem(LISTS.recordTypes, type.id, fields);
      const item = await this.g.getItem(LISTS.recordTypes, type.id);
      return map.toRecordType(item.id, item.fields);
    }
    const item = await this.g.createItem(LISTS.recordTypes, fields);
    return map.toRecordType(item.id, item.fields);
  }
  async removeType(id: string): Promise<void> {
    await this.g.deleteItem(LISTS.recordTypes, id);
  }

  /** TypeId is indexed, so filtering by type is a real query rather than a scan. */
  async records(typeId?: string): Promise<ICustomRecord[]> {
    const items = typeId
      ? await this.g.filterItems(LISTS.records, `fields/TypeIdLookupId eq ${Number(typeId)}`)
      : await this.g.listItems(LISTS.records);
    return items.map((i) => map.toCustomRecord(i.id, i.fields));
  }

  /* Promotion happens on write. Everything lives in the payload; the flagged
     values are copied into the four indexed columns so "expiring in 90 days
     across all types" stays one indexed query (§6.2). */
  async saveRecord(record: ICustomRecord): Promise<ICustomRecord> {
    const type = (await this.types()).find((t) => t.id === record.typeId);
    const merged = withPromotions(type, record, DEPOTS);

    const fields = map.customRecordFields(merged);
    if (record.id) {
      await this.g.updateItem(LISTS.records, record.id, fields);
      const item = await this.g.getItem(LISTS.records, record.id);
      return map.toCustomRecord(item.id, item.fields);
    }
    const item = await this.g.createItem(LISTS.records, fields);
    return map.toCustomRecord(item.id, item.fields);
  }

  async removeRecord(id: string): Promise<void> {
    await this.g.deleteItem(LISTS.records, id);
  }
}

/* ─────────────────────────── Cross-cutting ─────────────────────────── */

class StatusRepository implements IStatusService {
  constructor(private readonly g: GraphClient) {}
  /** Expired tiles are dropped here rather than in the UI, so a script that has
      stopped running produces a missing tile, not a permanently green one (§11). */
  async tiles(): Promise<IStatusTile[]> {
    const items = await this.g.listItems(LISTS.statusTiles);
    const now = Date.now();
    return items.filter((i) => map.tileIsLive(i.fields, now)).map((i) => map.toStatusTile(i.id, i.fields));
  }
}

/** A library item's FileRef is server-relative — "/sites/HirdBlueprint/..." —
 *  and the app is on a different origin, so it needs the site's host in front
 *  of it to be a link anybody can follow. The host is the first segment of the
 *  site id, which is the one part of that triple that is not a GUID. */
function fileUrl(fileRef: unknown, siteId: string): string | undefined {
  const ref = typeof fileRef === 'string' ? fileRef : '';
  const host = siteId.split(',')[0];
  return ref && host ? `https://${host}${ref}` : undefined;
}

class AttachmentRepository implements IAttachmentService {
  constructor(
    private readonly g: GraphClient,
    private readonly siteId: string,
  ) {}
  /** RecordId is indexed on the library, so this is a filter, not a scan (§6.4). */
  async forRecord(recordId: string): Promise<IAttachment[]> {
    if (!recordId) return [];
    const items = await this.g
      .filterItems(LISTS.files, `fields/RecordId eq '${recordId.replace(/'/g, "''")}'`)
      .catch(() => []);
    return items.map((i) => map.toAttachment(i.fields, fileUrl(i.fields.FileRef, this.siteId)));
  }

  async upload(recordId: string, recordType: string, file: File): Promise<IAttachment> {
    const written = await this.g.uploadFile(LISTS.files, file.name, file, {
      RecordId: recordId,
      RecordType: recordType,
    });
    /* Built from what came back rather than from the File that went up, so the
       name is the one the library actually used — a second "diagram.png" is
       filed as "diagram 1.png" and calling it otherwise would be a link that
       does not open. */
    return map.toAttachment(written.fields, written.webUrl);
  }
}

class DepotRepository implements IDepotService {
  list(): IDepot[] {
    return DEPOTS;
  }
  name(id: string): string {
    return depotName(id);
  }
}

class SettingsRepository implements ISettingsService {
  constructor(private readonly g: GraphClient) {}

  async all(): Promise<Record<string, string>> {
    const items = await this.g.listItems(LISTS.settings);
    const out: Record<string, string> = {};
    for (const i of items) {
      const key = typeof i.fields.Title === 'string' ? i.fields.Title : '';
      if (key) out[key] = typeof i.fields.Value === 'string' ? i.fields.Value : '';
    }
    return out;
  }

  /** One row per key, created on first write.
   *
   *  Matched on Title rather than held in a map, because two people editing
   *  settings in different tabs would otherwise each create their own row for
   *  the same key and the Function would read whichever came back first. */
  async set(key: string, value: string): Promise<void> {
    const existing = await this.g.filterItems(LISTS.settings, `fields/Title eq '${key.replace(/'/g, "''")}'`);
    if (existing[0]) await this.g.updateItem(LISTS.settings, existing[0].id, { Value: value });
    else await this.g.createItem(LISTS.settings, { Title: key, Value: value });
  }
}

class AccessRepository implements IAccessService {
  constructor(
    private readonly g: GraphClient,
    private readonly currentUser: ICurrentUser,
  ) {}

  async me(): Promise<ICurrentUser> {
    return this.currentUser;
  }
  async roles(): Promise<IRoleDefinition[]> {
    return (await this.g.listItems(LISTS.roles)).map((i) => map.toRole(i.fields));
  }
  async modules(): Promise<IModule[]> {
    return (await this.g.listItems(LISTS.modules)).map((i) => map.toModule(i.id, i.fields));
  }
  async setModuleEnabled(id: string, enabled: boolean): Promise<void> {
    const items = await this.g.listItems(LISTS.modules);
    const match = items.find((i) => map.toModule(i.id, i.fields).id === id);
    if (!match) throw new Error(`No module ${id}`);
    await this.g.updateItem(LISTS.modules, match.id, { Enabled: enabled });
  }
}

/* The sync is a nightly timer-triggered Function running app-only (§10). Nothing
   about it happens in the browser, so this reads its outcome and refuses to
   pretend it can start one until that Function exists. */
class SyncRepository implements ISyncService {
  constructor(
    private readonly g: GraphClient,
    private readonly getToken: TokenProvider,
    private readonly syncUrl: string,
  ) {}

  async lastRun(): Promise<{ at: string; ok: boolean; summary: string } | null> {
    const tiles = await this.g.listItems(LISTS.statusTiles).catch(() => []);
    const tile = tiles
      .map((i) => map.toStatusTile(i.id, i.fields))
      .find((t) => /sync/i.test(t.label) || /sync/i.test(t.src));
    if (!tile) return null;
    return {
      at: tile.when,
      ok: tile.tone === 'ok',
      summary: `${tile.label}: ${tile.val}`,
    };
  }

  /* Starts one, at last.
   *
   *  The caller's own Microsoft 365 token goes with the request — the same one
   *  this client already holds to read SharePoint — and the Function checks it
   *  with Graph. No function key, because a key a browser can send is a key
   *  anybody can read out of the page.
   *
   *  Takes about forty seconds, because it waits for the run rather than
   *  accepting and returning — the Functions host may stop an invocation once
   *  its response is sent, so a fire-and-forget sync is one that can be cut off
   *  halfway having reported success. When this resolves, the sync has actually
   *  happened. */
  async trigger(): Promise<void> {
    if (!this.syncUrl) {
      throw new Error(
        'The sync endpoint is not configured in this build. VITE_SYNC_URL should point at the Function App — the nightly runs are unaffected.',
      );
    }

    /* The scopes the app signs in with, not a narrower pair: the tenant has consented
       Sites.ReadWrite.All for this app, and asking for Sites.Read.All instead is a
       DIFFERENT permission that was never granted, so Microsoft stops with "Approval
       required" the first time somebody presses Sync. The Function only needs to read
       /me and the register as the caller, which this token does. */
    const token = await this.getToken(['User.Read', 'Sites.ReadWrite.All']);
    const res = await fetch(this.syncUrl, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}` },
    });

    if (res.ok) return;

    /* The Function answers refusals in words a person can act on — an expired
       session, an account outside the tenant, a run already going — so they are
       shown rather than replaced with a status code. */
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(
      body.error ?? `The sync could not be started (HTTP ${res.status}).`,
    );
  }
}

/* ─────────────────────────── Assembly ─────────────────────────── */

export interface SharePointServiceOptions {
  getToken: TokenProvider;
  siteId: string;
  currentUser: ICurrentUser;
  /** The Function App's /api/sync endpoint. Absent in a build that has not been
      told where the sync lives, which is a configuration problem rather than a
      broken feature — the nightly runs do not depend on it. */
  syncUrl?: string;
  /** func-hird-onboarding's /api base, and the HUB's client id — the Function's
      Easy Auth validates the token audience against that registration, not
      Blueprint's. Absent is a configuration problem rather than a broken
      feature: Hub Admin can still set people up. */
  onboardUrl?: string;
  hubClientId?: string;
  /* The Training Matrix's site and the two lists on it. Absent is a
     configuration problem, not a broken feature: every other tier still sets
     normally, and the departments control simply does not appear. */
  trainingSite?: ITrainingSite;
  /** The Onboarding app's site, for opening cases and reading role templates. */
  joinerSite?: IJoinerSite;
}

export function createSharePointServices(options: SharePointServiceOptions): ILedgerServices {
  const g = new GraphClient(options.getToken, options.siteId);

  return {
    people: new PeopleRepository(g),
    groups: new GroupRepository(g),
    devices: new DeviceRepository(g),
    directory: new DirectoryRepository(new DirectoryClient(options.getToken)),
    leave: new LeaveSyncClient(options.getToken),
    windDown: new OffboardClient(options.getToken),
    /* Only when the Training Matrix's site is configured. Offering the
       control without somewhere to write it is the fault the old hardcoded
       tier had: it reports success and changes nothing. */
    trainingScope: options.trainingSite
      ? new TrainingScopeRepository(new TrainingScopeClient(options.getToken, options.trainingSite))
      : undefined,
    joiners: options.joinerSite ? new JoinerClient(options.getToken, options.joinerSite) : undefined,
    onboarding: new OnboardingClient(
      options.getToken,
      options.onboardUrl ?? '',
      options.hubClientId ?? '',
    ),
    licences: new LicenceRepository(g),
    networks: new NetworkRepository(g),
    locations: new LocationRepository(g),
    applications: new ApplicationRepository(g),
    suppliers: new SupplierRepository(g),
    domains: new DomainRepository(g),
    backups: new BackupRepository(g),
    articles: new ArticleRepository(g),
    procedures: new ProcedureRepository(g),
    credentials: new CredentialRepository(g),
    audit: new AuditRepository(g),
    recordTypes: new RecordTypeRepository(g),
    status: new StatusRepository(g),
    attachments: new AttachmentRepository(g, options.siteId),
    depots: new DepotRepository(),
    settings: new SettingsRepository(g),
    access: new AccessRepository(g, options.currentUser),
    sync: new SyncRepository(g, options.getToken, options.syncUrl ?? ''),
  };
}

/* Kept as a named export because DepotId is part of the public shape above. */
export type { DepotId };
